import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';

interface SupportModalProps {
  isOpen: boolean;
  onClose: () => void;
  userEmail?: string;
}

const SupportModal: React.FC<SupportModalProps> = ({ isOpen, onClose, userEmail }) => {
  const [email, setEmail] = useState(userEmail || '');
  const [concerns, setConcerns] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [isSent, setIsSent] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSending(true);
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    console.log('Support Email Sent to envestaai@gmail.com:', { email, concerns });
    setIsSending(false);
    setIsSent(true);
    
    // Close after a short delay
    setTimeout(() => {
      onClose();
      // Reset state after closing
      setTimeout(() => {
        setIsSent(false);
        setConcerns('');
      }, 300);
    }, 2000);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div key="support-modal-wrapper" className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-black/75"
          />
          
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 15 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 15 }}
            className="relative w-full max-w-lg bg-[#3a3430] border border-white/[0.06] rounded-xl overflow-hidden max-h-[calc(100vh-2rem)] md:max-h-[calc(100vh-4rem)] flex flex-col text-white"
          >
            {/* Header */}
            <div className="px-6 py-5 border-b border-white/[0.06] bg-[#322D29] flex items-center justify-between shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-7 h-7 rounded bg-[#F26522] flex items-center justify-center text-white">
                  <i className="fas fa-headset text-xs"></i>
                </div>
                <div>
                  <h2 className="text-sm font-bold uppercase tracking-widest text-white font-heading">
                    Help & Feedback
                  </h2>
                  <p className="text-[9px] font-bold uppercase tracking-[0.2em] text-[#F26522] font-heading mt-0.5">
                    Customer Support
                  </p>
                </div>
              </div>
              <button 
                onClick={onClose}
                className="w-8 h-8 flex items-center justify-center text-white/40 hover:text-white transition-colors cursor-pointer rounded-lg hover:bg-white/5"
              >
                <i className="fas fa-times text-base"></i>
              </button>
            </div>

            {/* Body */}
            <div className="p-6 flex-1 overflow-y-auto custom-scrollbar">
              {isSent ? (
                <div className="py-12 flex flex-col items-center text-center space-y-4 animate-in zoom-in-95 duration-300">
                  <div className="w-14 h-14 rounded-xl bg-[#F26522]/15 text-[#F26522] border border-[#F26522]/25 flex items-center justify-center mb-1">
                    <i className="fas fa-check text-xl"></i>
                  </div>
                  <h3 className="text-sm font-bold uppercase tracking-widest text-white font-heading">
                    Message Sent Successfully
                  </h3>
                  <p className="text-xs font-medium text-white/60 tracking-wide max-w-[280px] font-dm">
                    Thank you for your feedback. Our team will review your message and respond to <span className="text-white font-bold">{email}</span> shortly.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-bold uppercase tracking-widest text-white/60 font-heading">User Email</label>
                    <div className="relative">
                      <i className="fas fa-envelope absolute left-4 top-1/2 -translate-y-1/2 text-white/30 text-xs"></i>
                      <input 
                        type="email"
                        required
                        placeholder="user@example.com"
                        className="w-full pl-10 pr-4 py-3 bg-[#322D29] border border-white/[0.08] text-white placeholder-white/30 text-sm font-medium rounded-lg outline-none focus:border-[#F26522] transition-colors font-dm"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-[10px] font-bold uppercase tracking-widest text-white/60 font-heading">Your Message or Feedback</label>
                    <textarea 
                      required
                      rows={4}
                      placeholder="Tell us what you need help with or share your feedback..."
                      className="w-full px-4 py-3 bg-[#322D29] border border-white/[0.08] text-white placeholder-white/30 text-sm font-medium rounded-lg outline-none focus:border-[#F26522] transition-colors resize-none font-dm"
                      value={concerns}
                      onChange={(e) => setConcerns(e.target.value)}
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isSending}
                    className="w-full bg-[#F26522] hover:bg-[#F26522]/90 text-white py-3.5 font-bold uppercase text-[11px] tracking-[0.2em] rounded-lg transition-colors font-heading active:scale-[0.99] disabled:opacity-50 flex items-center justify-center gap-2.5 cursor-pointer mt-2"
                  >
                    {isSending ? (
                      <>
                        <i className="fas fa-circle-notch fa-spin text-xs"></i>
                        Sending...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-paper-plane text-xs"></i>
                        Send Message
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-3 bg-[#322D29] border-t border-white/[0.06] flex justify-between items-center">
              <span className="text-[9px] font-bold uppercase tracking-[0.25em] text-white/40">
                Support Team: Active
              </span>
              <span className="text-[9px] font-bold uppercase tracking-[0.25em] text-white/40">
                Response ETA: &lt; 24H
              </span>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default SupportModal;
